function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import keysIn from 'lodash/keysIn';
import pick from 'lodash/pick';
import startsWith from 'lodash/startsWith';
import uniq from 'lodash/uniq';
import { validateForm } from 'helpers/form';
import DefaultValidator from 'libs/Validator';
import { useAction } from 'hooks';
import * as actions from './actions';
import { MODULE_ID } from './constants';
export const useIsFormNotCompleted = (formValues, validatorRules, { Validator = DefaultValidator } = {})=>useMemo(()=>{
        const requiredRules = {};
        const fields = keysIn(validatorRules);
        forEach(fields, (field)=>{
            if (startsWith(validatorRules[field], 'required')) {
                requiredRules[field] = 'required';
            }
        });
        const { errors } = validateForm(formValues, requiredRules, false, Validator);
        return !!errors;
    }, [
        formValues,
        validatorRules
    ]);
export const useIsPasswordValid = (formValues, validatorRules, { Validator = DefaultValidator } = {})=>{
    const password = get(formValues, 'values.password');
    return useMemo(()=>{
        const inputValidatorRules = {
            password: validatorRules.password
        };
        const { errors } = Validator.run({
            password
        }, inputValidatorRules, false);
        return !errors;
    }, [
        password
    ]);
};
export const useFieldValue = (formName, fieldId)=>useSelector((state)=>get(state, `${MODULE_ID}.forms.${formName}.values.${fieldId}`));
export const useFormValues = (formName)=>useSelector((state)=>get(state, `${MODULE_ID}.forms.${formName}`, null));
export const useFieldFormValues = (formName, fieldId)=>{
    const formPath = `${MODULE_ID}.forms.${formName}`;
    const selectFieldFormValues = useMemo(()=>createSelector([
            (state)=>get(state, `${formPath}.values.${fieldId}`),
            (state)=>get(state, `${formPath}.originalValues.${fieldId}`),
            (state)=>get(state, `${formPath}.errors.${fieldId}`),
            (state)=>get(state, `${formPath}.BEValidationErrors.${fieldId}`),
            (state)=>get(state, `${formPath}.contextData`),
            (state)=>get(state, `${formPath}.processing`)
        ], (value, originalValue, errors, BEErrors, contextData, processing)=>{
            const isNoField = [
                value,
                originalValue,
                errors,
                BEErrors
            ].every((param)=>param === undefined);
            if (isNoField) {
                return null;
            }
            return _object_spread({
                values: {
                    [fieldId]: value
                },
                processing: !!processing
            }, originalValue !== undefined ? {
                originalValues: {
                    [fieldId]: originalValue
                }
            } : {}, errors !== undefined ? {
                errors: {
                    [fieldId]: errors
                }
            } : {}, BEErrors !== undefined ? {
                BEValidationErrors: {
                    [fieldId]: BEErrors
                }
            } : {}, contextData !== undefined ? {
                contextData
            } : {});
        }), [
        formName,
        fieldId
    ]);
    return useSelector(selectFieldFormValues);
};
// TODO: Specify validatedValues type
export const useFormProcessing = (formName, onSubmit, { isToUnfold = false, onlyChanged = false, stopOnFirstError = true, Validator = DefaultValidator } = {})=>{
    const onProcessing = useAction(actions.startFormProcessing, formName);
    const onErrors = useAction(actions.setFormErrors, formName);
    return useCallback((formValues, validatorRules)=>{
        onProcessing();
        const { validatedValues, errors } = validateForm(formValues, validatorRules, isToUnfold, Validator, stopOnFirstError);
        onErrors(errors);
        let values = validatedValues;
        if (onlyChanged) {
            const originalValues = get(formValues, 'originalValues', {});
            const changedValuesKeys = uniq(keysIn(originalValues).map((k)=>k.split('.')[0]));
            values = pick(validatedValues, changedValuesKeys);
        }
        if (!errors && onSubmit) onSubmit(values);
    }, [
        onSubmit
    ]);
};
export const useValidatedSetFormValue = (formName, validatorRules, { stopOnFirstError = true, Validator = DefaultValidator } = {})=>{
    const setFormValue = useAction(actions.setFormValue, formName);
    return useCallback((input)=>{
        const inputValidatorRules = {
            [input.id]: validatorRules[input.id]
        };
        const { errors } = Validator.run({
            [input.id]: input.value
        }, inputValidatorRules, stopOnFirstError);
        setFormValue(input, errors);
    }, [
        validatorRules
    ]);
};
